import {
  makeStyles,
  TableBody,
  TableFooter,
  TableRow
} from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { PageheaderList_pageHeaders_edges_node } from "@saleor/pageheaders/types/PageheaderList";
import { PageheaderListUrlSortField } from "@saleor/pageheaders/urls";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface PageheaderListProps
  extends ListProps,
    ListActions,
    SortPage<PageheaderListUrlSortField> {
  pageheaders: PageheaderList_pageHeaders_edges_node[];
}

const numberOfColumns = 10;

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colName: {
        width: "auto"
      },
      colID: {
        width: 80
      },
      colTitle: {
        width: "auto"
      },
      colColor: {
        width: 180
      },
      colCreated: {
        width: 200
      }
    }
  }),
  { name: "PageheaderList" }
);

const PageheaderList: React.FC<PageheaderListProps> = ({
  pageheaders,
  disabled,
  isChecked,
  onNextPage,
  onPreviousPage,
  onRowClick,
  pageInfo,
  selected,
  sort,
  toggle,
  toggleAll,
  toolbar,
  onSort
}) => {
  const classes = useStyles({});
  const intl = useIntl();
  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={pageheaders}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === PageheaderListUrlSortField.id
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(PageheaderListUrlSortField.id)}
        >
          <FormattedMessage defaultMessage="ID" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          colSpan={2}
          direction={
            sort.sort === PageheaderListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(PageheaderListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="Name" description="Name Text" />
        </TableCellHeader>
        <TableCellHeader className={classes.colName} colSpan={2}>
          <FormattedMessage
            defaultMessage="User in Default Page"
            description="User in Default Page"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colName} colSpan={2}>
          <FormattedMessage defaultMessage="Use for" description="Use for" />
        </TableCellHeader>
        <TableCellHeader className={classes.colName} colSpan={2}>
          <FormattedMessage
            defaultMessage="InfoBar IsTop"
            description="InfoBar IsTop"
          />
        </TableCellHeader>

        <TableCellHeader
          className={classes.colName}
          colSpan={2}
          direction={
            sort.sort === PageheaderListUrlSortField.created
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(PageheaderListUrlSortField.created)}
        >
          <FormattedMessage
            defaultMessage="Created Date"
            description="Created Date"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          pageheaders,
          pageheader => {
            const isSelected = pageheader ? isChecked(pageheader.id) : false;

            return (
              <TableRow
                selected={isSelected}
                hover={!!pageheader}
                key={pageheader ? pageheader.id : "skeleton"}
                onClick={pageheader && onRowClick(pageheader.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(pageheader.id)}
                  />
                </TableCell>
                <TableCell className={classes.colID}>
                  {pageheader ? pageheader?.pageHeaderId : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colTitle} colSpan={2}>
                  {pageheader ? pageheader.name : <Skeleton />}
                </TableCell>
                <TableCell
                  className={classes.colActive}
                  data-test="isDefault"
                  data-test-active={maybe(() => pageheader.isDefault)}
                  colSpan={2}
                >
                  {pageheader &&
                  maybe(() => pageheader.isDefault !== undefined) ? (
                    <StatusLabel
                      label={
                        pageheader.isDefault
                          ? intl.formatMessage({
                              defaultMessage: "Yes",
                              description: "pageheader status"
                            })
                          : intl.formatMessage({
                              defaultMessage: "No",
                              description: "pageheader status"
                            })
                      }
                      status={pageheader.isDefault ? "success" : "error"}
                    />
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colActive}
                  data-test="Countdown"
                  data-test-active={maybe(() => pageheader.countdown)}
                  colSpan={2}
                >
                  {pageheader &&
                  maybe(() => pageheader.countdown !== undefined) ? (
                    <StatusLabel
                      label={
                        pageheader.located === "TOP"
                          ? pageheader.countdown
                            ? intl.formatMessage({
                                defaultMessage: "Old TimePicker",
                                description: "pageheader status"
                              })
                            : intl.formatMessage({
                                defaultMessage: "New InfoBar",
                                description: "pageheader status"
                              })
                          : pageheader.located === "CHECKOUT"
                          ? intl.formatMessage({
                              defaultMessage: "Checkout",
                              description: "pageheader status"
                            })
                          : pageheader.located
                      }
                      status={pageheader.countdown ? "neutral" : "success"}
                    />
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colActive}
                  data-test="isTop"
                  data-test-active={maybe(() => pageheader.isTop)}
                  colSpan={2}
                >
                  {pageheader && maybe(() => pageheader.isTop !== undefined) ? (
                    <StatusLabel
                      label={
                        pageheader.isTop
                          ? intl.formatMessage({
                              defaultMessage: "Yes",
                              description: "pageheader status"
                            })
                          : intl.formatMessage({
                              defaultMessage: "No",
                              description: "pageheader status"
                            })
                      }
                      status={pageheader.isTop ? "success" : "error"}
                    />
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>

                <TableCell
                  className={classes.colCreated}
                  align="left"
                  colSpan={2}
                >
                  {pageheader ? pageheader.created : <Skeleton />}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No Pageheader found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

export default PageheaderList;
